/**
 * ENHANCED WALL LOOKUP SYSTEM FOR COORDINATE COLLISION SAFETY
 * 
 * This module provides safe coordinate-based wall lookups that handle multiple 
 * walls at the same coordinates. It integrates with the shared wall system to 
 * ensure correct wall identification during resize and manipulation operations.
 */

import { Wall, Point, Room } from '../types';
import { WallWithTempId, sharedWallSystem } from './sharedWallCoordinateSystem';

export interface WallLookupOptions {
  preferredRoomId?: string;
  tolerance?: number;
  includeSharedWalls?: boolean;
  preferOriginalWalls?: boolean;
  returnAllMatches?: boolean;
}

export interface WallLookupResult {
  wall: WallWithTempId | null;
  allMatches: WallWithTempId[];
  hasMultipleMatches: boolean;
  isSharedWall: boolean;
  ownerRoomIds: string[];
  confidence: 'high' | 'medium' | 'low';
  warnings: string[];
}

export interface ResizeSafeWallLookup {
  targetWall: WallWithTempId;
  isAuthorizedForResize: boolean;
  sharedWithRooms: string[];
  safetyWarnings: string[];
}

/**
 * Enhanced wall lookup class that handles coordinate collisions safely
 */
export class EnhancedWallLookupSystem {
  private readonly DEFAULT_TOLERANCE = 0.1;
  
  /**
   * Find walls at specific coordinates with collision-safe handling
   */
  public findWallsAtCoordinates(
    coordinates: { start: Point; end: Point },
    allWalls: WallWithTempId[],
    options: WallLookupOptions = {}
  ): WallLookupResult {
    const {
      preferredRoomId,
      tolerance = this.DEFAULT_TOLERANCE,
      includeSharedWalls = true,
      preferOriginalWalls = false,
      returnAllMatches = true
    } = options;

    const warnings: string[] = [];
    
    // Find all walls that match the coordinates
    const matchingWalls = this.findCoordinateMatches(coordinates, allWalls, tolerance);
    
    if (matchingWalls.length === 0) {
      return {
        wall: null,
        allMatches: [],
        hasMultipleMatches: false,
        isSharedWall: false,
        ownerRoomIds: [],
        confidence: 'high',
        warnings: ['No walls found at specified coordinates']
      };
    }

    if (matchingWalls.length === 1) {
      const wall = matchingWalls[0];
      return {
        wall,
        allMatches: matchingWalls,
        hasMultipleMatches: false,
        isSharedWall: !!wall.isSharedWall,
        ownerRoomIds: this.extractOwnerRoomIds(wall),
        confidence: 'high',
        warnings: []
      };
    }

    // Multiple matches - apply selection logic
    warnings.push(`Found ${matchingWalls.length} walls at the same coordinates`);
    
    const selectedWall = this.selectBestWall(
      matchingWalls,
      preferredRoomId,
      preferOriginalWalls,
      includeSharedWalls
    );

    const confidence = this.calculateConfidence(matchingWalls, selectedWall, preferredRoomId);

    console.log('🔍 ENHANCED WALL LOOKUP:', {
      coordinateKey: this.generateCoordinateKey(coordinates),
      matchCount: matchingWalls.length,
      preferredRoomId,
      selectedWallId: selectedWall?.tempId,
      confidence,
      warningCount: warnings.length
    });

    return {
      wall: selectedWall,
      allMatches: returnAllMatches ? matchingWalls : (selectedWall ? [selectedWall] : []),
      hasMultipleMatches: true,
      isSharedWall: selectedWall?.isSharedWall || matchingWalls.some(w => w.isSharedWall),
      ownerRoomIds: selectedWall ? this.extractOwnerRoomIds(selectedWall) : [],
      confidence,
      warnings
    };
  }

  /**
   * Safe wall lookup specifically for resize operations
   */
  public findWallForResize(
    wallToResize: WallWithTempId,
    roomId: string,
    allWalls: WallWithTempId[]
  ): ResizeSafeWallLookup {
    const safetyWarnings: string[] = [];
    let isAuthorizedForResize = true;
    let sharedWithRooms: string[] = [];

    // Check if this is a shared wall
    const sharedGroup = sharedWallSystem.getSharedWallGroup(wallToResize.tempId || '');
    
    if (sharedGroup) {
      sharedWithRooms = sharedGroup.ownerRoomIds.filter(id => id !== roomId);
      
      if (!sharedGroup.ownerRoomIds.includes(roomId)) {
        isAuthorizedForResize = false;
        safetyWarnings.push(`Room ${roomId} is not authorized to resize shared wall ${wallToResize.tempId}`);
      }
      
      if (sharedWithRooms.length > 0) {
        safetyWarnings.push(`Resizing this wall will affect rooms: ${sharedWithRooms.join(', ')}`);
      }
    }

    // Check for coordinate collisions
    const lookupResult = this.findWallsAtCoordinates(
      { start: wallToResize.start, end: wallToResize.end },
      allWalls,
      { preferredRoomId: roomId, returnAllMatches: false }
    );

    if (lookupResult.hasMultipleMatches) {
      safetyWarnings.push('Multiple walls found at same coordinates - resize may affect unintended walls');
    }

    const targetWall = lookupResult.wall || wallToResize;

    console.log('🛡️ RESIZE SAFETY CHECK:', {
      wallId: wallToResize.tempId,
      roomId,
      isAuthorizedForResize,
      sharedWithRoomCount: sharedWithRooms.length,
      hasCoordinateCollisions: lookupResult.hasMultipleMatches,
      safetyWarningCount: safetyWarnings.length
    });

    return {
      targetWall,
      isAuthorizedForResize,
      sharedWithRooms,
      safetyWarnings
    };
  }

  /**
   * Find the most appropriate wall for a specific room
   */
  public findRoomSpecificWall(
    coordinates: { start: Point; end: Point },
    roomId: string,
    allWalls: WallWithTempId[]
  ): WallWithTempId | null {
    const result = this.findWallsAtCoordinates(coordinates, allWalls, {
      preferredRoomId: roomId,
      includeSharedWalls: true,
      returnAllMatches: false
    });

    if (result.confidence === 'low') {
      console.warn('⚠️ LOW CONFIDENCE WALL LOOKUP:', {
        roomId,
        coordinateKey: this.generateCoordinateKey(coordinates),
        matchCount: result.allMatches.length,
        warnings: result.warnings
      });
    }

    return result.wall;
  }

  /**
   * Batch lookup for multiple coordinates
   */
  public batchLookupWalls(
    coordinateList: Array<{ start: Point; end: Point }>,
    allWalls: WallWithTempId[],
    options: WallLookupOptions = {}
  ): WallLookupResult[] {
    return coordinateList.map(coordinates => 
      this.findWallsAtCoordinates(coordinates, allWalls, options)
    );
  }

  /**
   * Find coordinate matches with tolerance
   */
  private findCoordinateMatches(
    coordinates: { start: Point; end: Point },
    allWalls: WallWithTempId[],
    tolerance: number
  ): WallWithTempId[] {
    return allWalls.filter(wall => {
      return this.areCoordinatesEqual(coordinates, wall, tolerance) ||
             this.areCoordinatesEqualReversed(coordinates, wall, tolerance);
    });
  }

  /**
   * Select the best wall from multiple matches
   */
  private selectBestWall(
    matchingWalls: WallWithTempId[],
    preferredRoomId?: string,
    preferOriginalWalls?: boolean,
    includeSharedWalls?: boolean
  ): WallWithTempId | null {
    if (matchingWalls.length === 0) return null;

    // Priority 1: Exact room ownership match
    if (preferredRoomId) {
      const exactMatch = matchingWalls.find(wall => 
        wall.originatingRoomId === preferredRoomId
      );
      if (exactMatch) return exactMatch;

      // Priority 2: Assigned to preferred room
      const assignedMatch = matchingWalls.find(wall =>
        wall.assignedRoomIds?.includes(preferredRoomId)
      );
      if (assignedMatch) return assignedMatch;
    }

    // Priority 3: Original walls (non-shared) if preferred
    if (preferOriginalWalls) {
      const originalWall = matchingWalls.find(wall => !wall.isSharedWall);
      if (originalWall) return originalWall;
    }

    // Priority 4: Shared walls if allowed
    if (includeSharedWalls) {
      const sharedWall = matchingWalls.find(wall => wall.isSharedWall);
      if (sharedWall) return sharedWall;
    }

    // Fallback: First wall
    return matchingWalls[0];
  }

  /**
   * Calculate lookup confidence based on selection quality
   */
  private calculateConfidence(
    allMatches: WallWithTempId[],
    selectedWall: WallWithTempId | null,
    preferredRoomId?: string
  ): 'high' | 'medium' | 'low' {
    if (!selectedWall) return 'low';
    if (allMatches.length === 1) return 'high';

    // High confidence if we found exact room match
    if (preferredRoomId && 
        (selectedWall.originatingRoomId === preferredRoomId ||
         selectedWall.assignedRoomIds?.includes(preferredRoomId))) {
      return 'high';
    }

    // Medium confidence if we found a shared wall
    if (selectedWall.isSharedWall) return 'medium';

    // Low confidence for fallback selection
    return 'low';
  }

  /**
   * Extract all owner room IDs from a wall
   */
  private extractOwnerRoomIds(wall: WallWithTempId): string[] {
    const roomIds = new Set<string>();
    
    if (wall.originatingRoomId) roomIds.add(wall.originatingRoomId);
    if (wall.assignedRoomIds) {
      wall.assignedRoomIds.forEach(id => roomIds.add(id));
    }
    if (wall.parentRoomIds) {
      wall.parentRoomIds.forEach(id => roomIds.add(id));
    }

    return Array.from(roomIds);
  }

  /**
   * Generate coordinate key for comparison
   */
  private generateCoordinateKey(coordinates: { start: Point; end: Point }): string {
    const precision = 2;
    const start = `${coordinates.start.x.toFixed(precision)},${coordinates.start.y.toFixed(precision)}`;
    const end = `${coordinates.end.x.toFixed(precision)},${coordinates.end.y.toFixed(precision)}`;
    return start < end ? `${start}→${end}` : `${end}→${start}`;
  }

  /**
   * Check if coordinates are equal within tolerance
   */
  private areCoordinatesEqual(
    coord1: { start: Point; end: Point },
    coord2: { start: Point; end: Point },
    tolerance: number
  ): boolean {
    return Math.abs(coord1.start.x - coord2.start.x) < tolerance &&
           Math.abs(coord1.start.y - coord2.start.y) < tolerance &&
           Math.abs(coord1.end.x - coord2.end.x) < tolerance &&
           Math.abs(coord1.end.y - coord2.end.y) < tolerance;
  }

  /**
   * Check if coordinates are equal in reverse direction
   */
  private areCoordinatesEqualReversed(
    coord1: { start: Point; end: Point },
    coord2: { start: Point; end: Point },
    tolerance: number
  ): boolean {
    return Math.abs(coord1.start.x - coord2.end.x) < tolerance &&
           Math.abs(coord1.start.y - coord2.end.y) < tolerance &&
           Math.abs(coord1.end.x - coord2.start.x) < tolerance &&
           Math.abs(coord1.end.y - coord2.start.y) < tolerance;
  }

  /**
   * Validate wall lookup results for debugging
   */
  public validateLookupResults(
    results: WallLookupResult[],
    expectedRoomId?: string
  ): { isValid: boolean; issues: string[]; recommendations: string[] } {
    const issues: string[] = [];
    const recommendations: string[] = [];

    let lowConfidenceCount = 0;
    let multipleMatchCount = 0;
    let missingWallCount = 0;

    for (const result of results) {
      if (!result.wall) {
        missingWallCount++;
        continue;
      }

      if (result.confidence === 'low') {
        lowConfidenceCount++;
      }

      if (result.hasMultipleMatches) {
        multipleMatchCount++;
      }

      if (expectedRoomId && !result.ownerRoomIds.includes(expectedRoomId)) {
        issues.push(`Wall ${result.wall.tempId} does not belong to expected room ${expectedRoomId}`);
      }
    }

    if (lowConfidenceCount > results.length * 0.3) {
      issues.push(`High number of low confidence lookups: ${lowConfidenceCount}/${results.length}`);
      recommendations.push('Consider reviewing wall ownership assignments');
    }

    if (multipleMatchCount > results.length * 0.5) {
      issues.push(`High number of coordinate collisions: ${multipleMatchCount}/${results.length}`);
      recommendations.push('Consider running coordinate collision repair utility');
    }

    if (missingWallCount > 0) {
      issues.push(`Missing walls: ${missingWallCount}/${results.length}`);
      recommendations.push('Verify that all expected walls exist in the input array');
    }

    return {
      isValid: issues.length === 0,
      issues,
      recommendations
    };
  }
}

// Global instance
export const enhancedWallLookup = new EnhancedWallLookupSystem();

/**
 * Convenience function for safe wall lookup during resize operations
 */
export function findWallSafeForResize(
  wallToResize: WallWithTempId,
  roomId: string,
  allWalls: WallWithTempId[]
): { wall: WallWithTempId; canResize: boolean; warnings: string[] } {
  const result = enhancedWallLookup.findWallForResize(wallToResize, roomId, allWalls);
  
  return {
    wall: result.targetWall,
    canResize: result.isAuthorizedForResize,
    warnings: result.safetyWarnings
  };
}

/**
 * Convenience function for room-specific wall finding
 */
export function findWallForRoom(
  coordinates: { start: Point; end: Point },
  roomId: string,
  allWalls: WallWithTempId[]
): WallWithTempId | null {
  return enhancedWallLookup.findRoomSpecificWall(coordinates, roomId, allWalls);
}